import { useEffect, useState } from "react";
import { Truck, Broadcast, ArrowsClockwise, Warning, CheckCircle, MapPin, GpsFix, StopCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fleetApi, shiftApi } from "@/lib/api";
import { LLSelect } from "@/components/ui/ll-select";
import { usePhoneGPS } from "@/hooks/usePhoneGPS";

export default function ShiftPanel({ onShiftChange }) {
  const [assets, setAssets] = useState([]);
  const [shift, setShift] = useState({ on_shift: false, asset: null });
  const [pick, setPick] = useState("");
  const [working, setWorking] = useState(false);

  const gps = usePhoneGPS({
    assetId: shift.asset?.id,
    intervalMs: 30_000,
  });

  const reload = async () => {
    const [a, s] = await Promise.all([
      fleetApi.list().catch(() => []),
      shiftApi.get().catch(() => ({ on_shift: false, asset: null })),
    ]);
    setAssets(a);
    setShift(s);
    if (s.on_shift && s.asset?.id) setPick(s.asset.id);
  };

  useEffect(() => { reload(); }, []);

  // Auto-start GPS when shift becomes active
  useEffect(() => {
    if (shift.on_shift && shift.asset?.id && !gps.active) {
      gps.start();
    }
    if (!shift.on_shift && gps.active) {
      gps.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift.on_shift, shift.asset?.id]);

  const startShift = async () => {
    if (!pick) return toast.error("Pick a truck first");
    setWorking(true);
    try {
      const asset = await shiftApi.start(pick);
      setShift({ on_shift: true, asset });
      toast.success(`On shift in ${asset.name}. GPS broadcasting…`);
      onShiftChange?.(true, asset);
      // start() runs via useEffect once shift state flips
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start shift");
    } finally {
      setWorking(false);
    }
  };

  const endShift = async () => {
    setWorking(true);
    try {
      gps.stop();
      await shiftApi.end();
      setShift({ on_shift: false, asset: null });
      toast.success("Shift ended");
      onShiftChange?.(false, null);
    } catch (e) {
      toast.error("Could not end shift");
    } finally {
      setWorking(false);
    }
  };

  const trucks = assets.filter((a) => a.type === "truck" || a.type === "loader" || a.type === "excavator");

  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a] mb-4" data-testid="shift-panel">
      <div className="border-b border-[#2a2a2a] px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Broadcast size={14} className={shift.on_shift ? "text-[#22c55e]" : "text-[#71717a]"} weight="fill" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold">
            {shift.on_shift ? "On Shift · GPS Live" : "Off Shift"}
          </span>
        </div>
        {shift.on_shift && (
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono">
            <span className="text-[#22c55e]">● {gps.count} pings</span>
            {gps.last?.accuracy != null && (
              <span className="text-[#71717a]">±{Math.round(gps.last.accuracy)}m</span>
            )}
          </div>
        )}
      </div>

      <div className="p-4">
        {!shift.on_shift ? (
          <>
            <div className="text-xs text-[#a1a1aa] mb-3 leading-relaxed">
              Pick the truck you're driving today. Your phone will broadcast GPS to dispatch every 30 seconds while you're on shift.
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
              <div className="flex-1">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-1">Your truck</label>
                <LLSelect
                  value={pick}
                  onValueChange={setPick}
                  placeholder="Select an asset…"
                  data-testid="shift-asset-picker"
                  options={trucks.map((t) => ({
                    value: t.id,
                    label: `${t.name}${t.driver ? ` · ${t.driver}` : ""}${t.on_shift_driver_email ? " (in use)" : ""}`,
                    disabled: !!t.on_shift_driver_email,
                  }))}
                />
              </div>
              <button
                onClick={startShift}
                disabled={!pick || working || !gps.supported}
                className="px-5 py-3 bg-[#22c55e] text-black hover:bg-[#16a34a] disabled:opacity-40 text-[10px] uppercase tracking-[0.25em] font-black flex items-center justify-center gap-2"
                data-testid="start-shift-btn"
              >
                {working ? <ArrowsClockwise size={12} className="animate-spin" /> : <GpsFix size={12} weight="fill" />}
                Start Shift
              </button>
            </div>
            {!gps.supported && (
              <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#ef4444] flex items-center gap-1">
                <Warning size={10} /> GPS not supported on this device
              </div>
            )}
            {gps.permission === "denied" && (
              <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#ef4444] flex items-center gap-1">
                <Warning size={10} /> Location permission blocked. Enable in browser settings.
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Truck size={18} className="text-[#ff8c00]" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">Driving</div>
                  <div className="font-heading font-bold tracking-tight">{shift.asset?.name}</div>
                </div>
              </div>
              <button
                onClick={endShift}
                disabled={working}
                className="px-4 py-2 border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white text-[10px] uppercase tracking-[0.25em] font-black flex items-center gap-2"
                data-testid="end-shift-btn"
              >
                {working ? <ArrowsClockwise size={12} className="animate-spin" /> : <StopCircle size={12} weight="fill" />}
                End Shift
              </button>
            </div>

            {gps.last && (
              <div className="grid grid-cols-3 gap-2 text-center mb-2">
                <Stat label="Latitude" value={gps.last.lat.toFixed(5)} />
                <Stat label="Longitude" value={gps.last.lng.toFixed(5)} />
                <Stat label="Speed" value={gps.last.speed != null ? `${Math.round(gps.last.speed * 2.23694)} mph` : "—"} />
              </div>
            )}

            {gps.error && (
              <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#ef4444] flex items-center gap-1">
                <Warning size={10} weight="fill" /> {gps.error}
              </div>
            )}
            {gps.queued && (
              <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#4682b4] flex items-center gap-1">
                <MapPin size={10} weight="fill" /> Queued for sync — no signal
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#1a1a1a] py-2 px-1">
      <div className="font-mono font-bold text-sm">{value}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717a] mt-0.5">{label}</div>
    </div>
  );
}
